"use client";
import { feedStyles } from "./forum-feed.styles";

import type {
  ForumFeedState,
  ForumSort,
  ForumVote,
  ForumComposePost,
} from "../forum.types";
import type { ForumLayoutProps } from "../forum-layout";
import { ForumFeedHeader } from "./forum-feed-header";
import { ForumFeedResults } from "./forum-feed-results";
import { useRef, useState, useLayoutEffect } from "react";
import * as stylex from "@stylexjs/stylex";
import { styles } from "../forum.styles";
import { ForumLayout } from "../forum-layout";
import { RefreshFeedback } from "../forum-refresh-feedback";
import { SegmentedControl, Button, Spinner } from "@softmax/design-system";
import { PostComposer } from "./forum-post-composer";

export type ForumFeedViewProps = ForumLayoutProps &
  Readonly<{
    showHeader?: boolean;
    lede?: string | undefined;
    state: ForumFeedState;
    sort: ForumSort;
    onSortChange(sort: ForumSort): void;
    onRefresh(): void;
    onLoadMore(): void;
    onVote({
      postId,
      direction,
    }: Readonly<{ postId: string; direction: 1 | -1 }>): void;
    votes?: Readonly<Record<string, ForumVote>>;
    postHref(postId: string): string;
    compose?: ForumComposePost | undefined;
  }>;

export function ForumFeedView(props: ForumFeedViewProps) {
  const {
    layout = "page",
    showHeader = true,
    description,
    homeHref,
    wikiRail,
    name,
    lede,
    state,
    sort,
    onSortChange,
    onRefresh,
    onLoadMore,
    onVote,
    votes,
    postHref,
    renderLink,
    compose,
  } = props;
  const feedArea = useRef<HTMLDivElement>(null);
  const [feedHeight, setFeedHeight] = useState(240);
  useLayoutEffect(() => {
    const element = feedArea.current;

    if (state.status !== "ready" || !element) return;

    const height = element.getBoundingClientRect().height;
    setFeedHeight((current) => Math.max(current, height));
  }, [state]);
  const refreshTrigger = useRef<HTMLButtonElement>(null);
  const retry = () => {
    refreshTrigger.current?.focus();
    onRefresh();
  };
  const Container = layout === "page" ? "main" : "section";
  return (
    <Container
      id={layout === "page" ? "main" : undefined}
      {...stylex.props(layout === "page" ? styles.page : styles.embedded)}
    >
      {showHeader && (
        <ForumFeedHeader layout={layout} name={name} lede={lede} />
      )}
      <ForumLayout
        description={description}
        homeHref={homeHref}
        wikiRail={wikiRail}
        renderLink={renderLink}
      >
        <div {...stylex.props(styles.toolbar)}>
          <SegmentedControl
            aria-label="Sort posts"
            value={sort}
            onValueChange={onSortChange}
            items={[
              { value: "hot", label: "Hot" },
              { value: "new", label: "New" },
              { value: "top", label: "Top" },
            ]}
          />
          {layout === "page" && (
            <Button
              ref={refreshTrigger}
              aria-disabled={state.status === "ready" && state.refreshing}
              onClick={() => {
                if (state.status !== "ready" || !state.refreshing) onRefresh();
              }}
            >
              Refresh
            </Button>
          )}
        </div>
        {compose && <PostComposer compose={compose} />}
        <div
          ref={feedArea}
          {...stylex.props(feedStyles.feedHeight(feedHeight))}
          aria-busy={state.status === "loading"}
        >
          {state.status === "loading" && (
            <output {...stylex.props(styles.loading)}>
              <Spinner />
              Loading the forum…
            </output>
          )}
          {state.status === "error" && (
            <RefreshFeedback
              refreshing={false}
              error={state.message}
              onRefresh={retry}
            />
          )}
          {state.status === "ready" && (
            <ForumFeedResults
              state={state}
              votes={votes}
              onVote={onVote}
              postHref={postHref}
              renderLink={renderLink}
              onLoadMore={onLoadMore}
              onRefresh={retry}
            />
          )}
        </div>
      </ForumLayout>
    </Container>
  );
}
